import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { finalize } from 'rxjs/operators';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { environment } from '../../../environments/environment';

interface ThemeColor {
  name: string;
  hex: string;
}

@Component({
  selector: 'app-org-branding',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressBarModule,
  ],
  templateUrl: './org-branding.component.html',
  styleUrl: './org-branding.component.scss',
})
export class OrgBrandingComponent {
  @Input() organizationId!: string;
  @Output() stepComplete = new EventEmitter<void>();
  @Output() stepSkipped = new EventEmitter<void>();

  themeColors: ThemeColor[] = [
    { name: 'Chareety Blue', hex: '#2B7CB8' },
    { name: 'Indigo',        hex: '#3949AB' },
    { name: 'Purple',        hex: '#6A1B9A' },
    { name: 'Teal',          hex: '#00838F' },
    { name: 'Green',         hex: '#2E7D32' },
    { name: 'Red',           hex: '#C62828' },
    { name: 'Orange',        hex: '#E65100' },
    { name: 'Slate',         hex: '#455A64' },
  ];

  selectedColor = '#2B7CB8';

  logoPreviewUrl = '';
  logoLocation = '';
  logoUploading = false;
  logoProgress = 0;

  bgPreviewUrl = '';
  bgLocation = '';
  bgUploading = false;
  bgProgress = 0;

  saving = false;

  constructor(
    private storage: AngularFireStorage,
    private api: OnboardingApiService,
    private snackBar: MatSnackBar,
  ) {}

  selectColor(hex: string): void {
    this.selectedColor = hex;
  }

  private readonly ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
  private readonly MAX_IMAGE_SIZE_MB = 5;

  private validateImageFile(file: File): boolean {
    if (!this.ALLOWED_IMAGE_TYPES.includes(file.type)) {
      this.snackBar.open('Invalid file type. Allowed: PNG, JPEG, WebP, SVG.', 'Close', { duration: 5000 });
      return false;
    }
    if (file.size > this.MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      this.snackBar.open(`File too large. Maximum size is ${this.MAX_IMAGE_SIZE_MB} MB.`, 'Close', { duration: 5000 });
      return false;
    }
    return true;
  }

  onLogoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!this.validateImageFile(file)) return;

    this.logoUploading = true;
    const ext = file.name.split('.').pop() || 'png';
    const path = `organizations/${this.organizationId}/logo.${ext}`;
    const ref = this.storage.ref(path);
    const task = this.storage.upload(path, file);

    task.percentageChanges().subscribe(p => { this.logoProgress = Math.round(p ?? 0); });

    task.snapshotChanges().pipe(
      finalize(() => {
        ref.getDownloadURL().subscribe(url => {
          this.logoPreviewUrl = url;
          this.logoLocation = `gs://${environment.firebaseConfig.storageBucket}/${path}`;
          this.logoUploading = false;
        });
      })
    ).subscribe();
  }

  onBackgroundSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!this.validateImageFile(file)) return;

    this.bgUploading = true;
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `organizations/${this.organizationId}/kiosk-background.${ext}`;
    const ref = this.storage.ref(path);
    const task = this.storage.upload(path, file);

    task.percentageChanges().subscribe(p => { this.bgProgress = Math.round(p ?? 0); });

    task.snapshotChanges().pipe(
      finalize(() => {
        ref.getDownloadURL().subscribe(url => {
          this.bgPreviewUrl = url;
          this.bgLocation = `gs://${environment.firebaseConfig.storageBucket}/${path}`;
          this.bgUploading = false;
        });
      })
    ).subscribe();
  }

  save(): void {
    this.saving = true;
    this.api.saveBranding(this.organizationId, {
      themeColor: this.selectedColor,
      ...(this.logoLocation && { logoLocation: this.logoLocation }),
      ...(this.bgLocation && { kioskBackground: this.bgLocation }),
    }).subscribe({
      next: () => {
        this.saving = false;
        this.stepComplete.emit();
      },
      error: (err) => {
        console.error('Failed to save branding:', err);
        this.snackBar.open('Error saving branding — try again', 'Close', { duration: 4000 });
        this.saving = false;
      },
    });
  }

  skip(): void {
    this.stepSkipped.emit();
  }
}
